import { Controller } from 'stimulus'
import { useDebounce, useDispatch } from 'stimulus-use'
import template from 'lodash.template'
import { keys } from '@app/components/atomic/constants'
import { showLoader } from '@app/javascript/classes/loaderExtensions'
import Rails from '@rails/ujs'

export default class extends Controller {
  static targets = ['submitButton', 'form', 'issues', 'labels', 'actionTypes', 'linkedContentStakeholders', 'selectionsList', 'selectionPillTemplate', 'selectedIds']
  static debounces = ['triggerFilter']

  connect() {
    useDebounce(this, { wait: 800 })
    useDispatch(this)
    this.formTarget.addEventListener('filters-container:filterCleared', this.triggerFilter.bind(this), {})

    const issuesInputBox = this.issuesTarget.querySelector('div[class="c-select-multiple__input"]')
    const labelsInputBox = this.labelsTarget.querySelector('div[class="c-select-multiple__input"]')
    const actionTypesInputBox = this.actionTypesTarget.querySelector('div[class="c-select-multiple__input"]')

    issuesInputBox.click()
    labelsInputBox.click()
    actionTypesInputBox.click()
  }

  triggerFilter(event) {
    showLoader('#actions-loader')
    this.submitButtonTarget.click()
    this.dispatch('formTriggered', {
      filters_applied: $.deparam($(this.formTarget).serialize())
    })
    Rails.ajax({
      type: 'GET',
      url: '/actions/active_filters',
      dataType: 'script',
      data: $(this.formTarget).serialize()
    })
  }

  uncheckAll() {
    this.actionSelectController()?.uncheckAll()
  }

  actionSelectController() {
    return this.application.getControllerForElementAndIdentifier(this.actionSelectControllerElement(), 'entity-select')
  }

  actionSelectControllerElement() {
    return document.querySelector('#entity-select')
  }

  issueSelected(event) {
    const selectedIssue = event.detail.name
    const selectedInput = this._issuesInputs().find(i => i.dataset.selectMultipleComponentMenuItemPillLabel === selectedIssue)
    const clearButton = this.issuesTarget.querySelector('button[id="issuesClearButton"]')
    const applyButton = this.issuesTarget.querySelector('button[data-test-id="submit submit_button"]')
    if (selectedInput) {
      clearButton.click()
      selectedInput.click()
      applyButton.click()
    }
  }

  labelSelected(event) {
    const selectedLabel = event.detail.name
    const selectedInput = this._labelsInputs().find(i => i.dataset.selectMultipleComponentMenuItemPillLabel === selectedLabel)
    const clearButton = this.labelsTarget.querySelector('button[id="labelsClearButton"]')
    const applyButton = this.labelsTarget.querySelector('button[data-test-id="submit submit_button"]')
    if (selectedInput) {
      clearButton.click()
      selectedInput.click()
      applyButton.click()
    }
  }

  actionTypeSelected(event) {
    const selectedType = event.detail.name
    const selectedInput = this._actionTypesInputs().find(i => i.dataset.selectMultipleComponentMenuItemPillLabel === selectedType)
    const applyButton = this.actionTypesTarget.querySelector('button[data-test-id="submit submit_button"]')
    if (selectedInput) {
      selectedInput.click()
      applyButton.click()
    }
  }

  gotoPreview(event) {
    const params = $.deparam($(this.formTarget).serialize()).search_attributes
    if (this.selectedIdsTarget.value) {
      params.actions_ids = this.selectedIdsTarget.value.split(',')
    }
    window.location.href = '/actions_report_preview?' + $.param(params)
  }

  _issuesInputs() {
    return Array.from(this.issuesTarget.querySelectorAll('input[name="search_attributes[issues_ids][]"]'))
  }

  _labelsInputs() {
    return Array.from(this.labelsTarget.querySelectorAll('input[name="search_attributes[labels_ids][]"]'))
  }

  _actionTypesInputs() {
    return Array.from(this.actionTypesTarget.querySelectorAll('input[name="search_attributes[action_types_ids][]"]'))
  }

  stakeholderSelected(event) {
    const linkedFilter = this.linkedContentStakeholdersTarget
    const clearButton = linkedFilter.querySelector('button[id="linkedContentStakeholdersClearButton"]')
    clearButton.click()
    clearButton.removeAttribute('disabled')
    const pillScript = linkedFilter.querySelector('script').innerHTML
    const pillTemplate = template(pillScript)
    const selectedStakeholder = event.detail.name
    const pillData = {
      text: selectedStakeholder,
      index: 1,
      value: event.detail.value,
      name: 'search_attributes[linked_item_ids][]',
      party_code: null
    }
    const pill = pillTemplate(pillData)
    const selectionsList = linkedFilter.querySelector('ol[data-input-name="search_attributes[linked_item_ids][]"]')
    $(selectionsList).append(pill)
    const value = pillData.value
    this.dispatch('optionSelected', { value })
    linkedFilter.classList.add('c-filter-active')
  }

  /**
   * The Actions "quantity" buttons are coded as radio inputs with the button role bolted on,
   * so that assistive technology will recognize them as toggle buttons.
   * In order to make these radio buttons behave like toggle buttons, too,
   * we need to adjust how they behave to certain keystrokes.
   * @param {KeyboardEvent} event
   */
  handleQuantityButtonKeydown(event) {
    // Grab event.key for IE11
    const key = event.code || event.key

    if (key === keys.TAB && event.shiftKey && $(event.target).parent().is(':not(:first-of-type)')) {
      event.preventDefault()
      $(event.target).parent().prevAll('.atom-input-radio-and-label').first().find('input').focus()
    } else if (key === keys.TAB && !event.shiftKey && !$(event.target).parent().is(':last-of-type')) {
      // Tab to the next quantity button, or the next interactive element in the focus order
      event.preventDefault()
      $(event.target).parent().nextAll('.atom-input-radio-and-label').first().find('input').focus()
    } else if ([keys.LEFT, keys.RIGHT, keys.DOWN, keys.UP].includes(key)) {
      // Prevent the user from making a new selection when they press the right or left arrow key,
      // and prevent the user from navigating across options when they press the down or up arrow key
      event.preventDefault()
    } else if (key === keys.ENTER) {
      // Re-create toggle button functionality
      $(event.target).trigger('click')
    }
  }

  /**
   * When the user tabs on to the first quantity selection button,
   * prevent the default event.
   * @param {KeyboardEvent} event
   */
  handleQuantityButtonKeyup(event) {
    // Grab event.key for IE11
    const key = event.code || event.key

    if (key === keys.TAB && !$(event.target).parent().is(':first-of-type')) {
      // Tab to the next quantity button, or the next interactive element in the focus order
      event.preventDefault()
    }
  }
}
